// File for all API/GraphQL calls
import axios from 'axios'
import {
  ContactFormParams,
  FuneralType,
  Graveyard,
  MandateForm,
  OfferInput,
  Order,
  Product,
  ProductType,
  WizardInput,
  ContactDetailInput
} from '../resources'

const url = process.env.REACT_APP_API

export const backend = {
  findGraveyardByName: (
    funeralType: FuneralType,
    key: string,
    callback: (result: any) => void
  ) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                    findGraveyardByName(name: "${key}", funeralType: ${funeralType}){
                        name
                        alreadyOwnGrave
                        address
                        region
                        uuid
                        country
                        latitude
                        longitude
                    }
                  }
                  `
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.findGraveyardByName)
        : console.error(res.data.errors)
    )
  },

  findLocation: (key: string, callback: (result: any) => void) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                    findLocation(key: "${key}"){
                        province
                        city
                        available
                        zipCode,
                        latitude,
                        longitude,
                        displayCategories
                    }
                  }
                  `
      }
    }).then((res) =>
      res.data.data.findLocation !== null
        ? callback(
            res.data.data.findLocation.sort((a, b) => {
              // Sorts the result based on the length of the city name
              // Slicing is necessary as Vienna is stored with the district
              // in () which often puts it last
              let containsSpace = a.city.indexOf(' ')
              const valueA =
                containsSpace === -1 ? a.city : a.city.slice(0, containsSpace)
              containsSpace = b.city.indexOf(' ')
              const valueB =
                containsSpace === -1 ? b.city : b.city.slice(0, containsSpace)
              return valueA.length - valueB.length
            })
          )
        : console.error(res.data.errors)
    )
  },

  findOfferByUuid: (uuid: string, callback: (result: OfferInput) => void) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                    findOfferByUuid(uuid: "${uuid}") {
                      uuid
                      location{
                          city
                          available
                          zipCode
                          province
                          latitude
                          longitude
                          displayCategories
                          label
                      }
                      comments
                      orderId
                      creationDate
                      planningReason
                      products {
                        name
                        uuid
                        shortDescription
                        longDescription
                        prices {
                          description
                          currency
                          amount
                        }
                        photos
                        quantity
                        productType
                        total {
                            amount
                        }
                        externalUrl
                        status
                      }
                      externalFees {
                        uuid
                        name
                        description
                        price {
                          amount
                        }
                        orderPriority
                      }
                      customerAgent{
                        name
                        photo
                      }
                      totalAmountFees {
                        amount
                      }
                      totalAmount {
                        amount
                      }
                      grandTotal {
                        amount
                      }
                      discounts {
                        name
                        shortDescription
                        longDescription
                        prices {
                          description
                          currency
                          amount
                        }
                      }  
                      interests {
                        uuid
                        name
                        shortDescription
                        longDescription
                        photos
                      }
                      provision{
                        timeFrame
                        progress
                        provisionReason
                        includeTrustAccountCosts
                      }
                      contactDetails{
                        greeting
                        firstName
                        lastName
                        email
                        phone
                      }
                      funeralType
                      offerDownloadUrl
                      graveyard{
                        name
                        alreadyOwnGrave
                        address
                        latitude
                        longitude
                        region
                        uuid
                        country
                        zipCode
                      }
                      appointment {
                        type
                        date
                        time
                      }
                      documents {
                        type
                        data {
                          key
                          value
                        }
                      }
                      orderConfirmed
                      interestedInFinancing
                      testOffer
                      syncWithZoho
                      sendNotifications
                    }
                  }
                  `
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.findOfferByUuid)
        : console.error(res.data.errors)
    )
  },


  findOfferByOrderId: (orderId: string, callback: (result: Order) => void) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
          findOfferByOrderId(orderId: "${orderId}") {
                      uuid
                      orderId
                      contactDetails {
                        greeting,
                        firstName,
                        lastName,
                        email,
                        phone,
                      },
                      customerAgent{
                        name
                        photo
                      }
                      planningReason
                      location {
                        city
                        label
                        zipCode
                        latitude
                        longitude
                        displayCategories
                        province
                        available
                      }
                      products {                      
                        uuid
                        name
                        total{
                          description
                          currency
                          amount
                        }
                        prices{
                          description
                          currency
                          amount
                        }
                        shortDescription
                        longDescription
                        photos
                        quantity
                        productType
                        orderPriority
                        externalUrl
                        status
                      }
                    }
                  }
                  `
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.findOfferByOrderId)
        : console.error(res.data.errors)
    )
  },

  



  saveOffer: (
    offerInput: OfferInput,
    sendOfferFinished: boolean,
    callback: (result: OfferInput) => void
  ) => {
    const saveMutation = `mutation {
            saveOffer(offer: {
              ${
                offerInput.comments
                  ? `comments:  ${JSON.stringify(offerInput.comments)}`
                  : ''
              }
              uuid: "${offerInput.uuid}"
              offerDownloadUrl: "${offerInput.offerDownloadUrl}"
              products: ${JSON.stringify(offerInput.products).replace(
                /"([^(")"]+)":/g,
                '$1:'
              )}
              interests: ${
                offerInput.interests.length
                  ? JSON.stringify(offerInput.interests).replace(
                      /"([^(")"]+)":/g,
                      '$1:'
                    )
                  : '[]'
              }
              graveyard: {
                ${
                  offerInput.graveyard.uuid === null
                    ? `
                      ${
                        offerInput.graveyard.name
                          ? `name: "${offerInput.graveyard.name}"`
                          : ''
                      }
                      ${
                        offerInput.graveyard.zipCode
                          ? `zipCode: "${offerInput.graveyard.zipCode}"`
                          : ''
                      }
                      ${
                        offerInput.graveyard.region
                          ? `region: "${offerInput.graveyard.region}"`
                          : ''
                      }
                      ${
                        offerInput.graveyard.country
                          ? `country: "${offerInput.graveyard.country}"`
                          : ''
                      }
                      ${
                        offerInput.graveyard.address
                          ? `address: "${offerInput.graveyard.address}"`
                          : ''
                      }`
                    : `uuid: "${offerInput.graveyard.uuid}"`
                }
                alreadyOwnGrave: ${!!offerInput.graveyard.alreadyOwnGrave}
              }
              location: {
                province: "${offerInput.location.province}"
                city: "${offerInput.location.city}"
                zipCode: "${offerInput.location.zipCode}"
                latitude: "${offerInput.location.latitude}"
                longitude: "${offerInput.location.longitude}"
              }
              funeralType: ${offerInput.funeralType}
              planningReason:  ${offerInput.planningReason}
              documents: ${
                offerInput.documents.length > 0
                  ? JSON.stringify(offerInput.documents).replace(
                      /"([^(")"]+)":/g,
                      '$1:'
                    )
                  : '[]'
              }
              interestedInFinancing: ${!!offerInput.interestedInFinancing}
              contactDetails: {
                greeting: ${offerInput.contactDetails.greeting}
                firstName:  "${offerInput.contactDetails.firstName}"
                lastName: "${offerInput.contactDetails.lastName}"
                email: "${offerInput.contactDetails.email}"
                phone: "${offerInput.contactDetails.phone}"
              }
              syncWithZoho: ${offerInput.syncWithZoho}
              sendNotifications: ${offerInput.sendNotifications}              
            }, sendOfferFinished: ${sendOfferFinished}) {
              uuid
            }
          }`

    return axios({
      url,
      method: 'post',
      data: {
        query: saveMutation
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.saveOffer)
        : console.error(res.data.errors)
    )
  },

  createOffer: (
    wizardInput: WizardInput,
    callback: (result: OfferInput) => void
  ) => {
    const deceasedLocation = `deceasedLocation: {
      locationType: ${wizardInput.deceasedLocation.locationType}
      street: ${wizardInput.deceasedLocation.street}
      number: ${wizardInput.deceasedLocation.number}
      zipCode: ${wizardInput.deceasedLocation.zipCode}
      city: ${wizardInput.deceasedLocation.city}
      country: ${wizardInput.deceasedLocation.country}
    }`
    const provision = `provision: {
            provisionReason: ${wizardInput.provision.provisionReason}
            progress: ${wizardInput.provision.progress}
            timeFrame: ${wizardInput.provision.timeFrame}
            birthYear: "${wizardInput.provision.birthYear}"
          }
        `
    let zipCode = null
    let city = null
    let label = null
    if (wizardInput.location.zipCode)
      zipCode = `"${wizardInput.location.zipCode}"`
    if (wizardInput.location.city) city = `"${wizardInput.location.city}"`
    if (wizardInput.location.label) label = `"${wizardInput.location.label}"`
    const location = `location: {
            city: ${city}
            label: ${label}
            zipCode: ${zipCode}
            longitude: "${wizardInput.location.longitude}"
            latitude: "${wizardInput.location.latitude}"
          }
        `

    const createMutation = `mutation{
            createOffer(wizard: {
                sendNotifications: ${wizardInput.sendNotifications}
                syncWithZoho: ${wizardInput.syncWithZoho}
                testingModeEnabled: ${wizardInput.testingModeEnabled}
                gclId: ${wizardInput.gclId ? `"${wizardInput.gclId}"` : null}
                gaClientId: ${wizardInput.gaClientId ? `"${wizardInput.gaClientId}"` : null}
                previousPage: ${
                  wizardInput.previousPage
                    ? `"${wizardInput.previousPage}"`
                    : null
                }                
                ${location}
                funeralType: ${wizardInput.funeralType}
                planningReason:  ${wizardInput.planningReason}
                ${
                  wizardInput.provision.provisionReason !== null
                    ? provision
                    : ''
                }
                ${
                  wizardInput.deceasedLocation.locationType !== null
                    ? deceasedLocation
                    : ''
                }
                beneficiary: ${
                  wizardInput.beneficiary ? wizardInput.beneficiary : null
                }
                contactDetails: {
                    greeting: ${wizardInput.contactDetails.greeting}
                    firstName:  "${wizardInput.contactDetails.firstName}"
                    lastName: "${wizardInput.contactDetails.lastName}"
                    email: "${wizardInput.contactDetails.email}"
                    phone: "${wizardInput.contactDetails.phone}"
                }
              }) {
                uuid
                creationDate
                orderId
                orderConfirmed
                interestedInFinancing
                offerDownloadUrl
                discounts {
                  shortDescription
                  longDescription
                  prices {
                    description
                    currency
                    amount
                  }
                }
                location {
                  zipCode
                  city
                  latitude
                  longitude
                  province
                  label
                }
                totalAmountFees {
                  amount
                }
                externalFees {
                  uuid
                  name
                  description
                  price {
                    amount
                  }
                }
                products {
                  name
                  uuid
                  shortDescription
                  longDescription
                  prices {
                    description
                    currency
                    amount
                  }
                  photos
                  quantity
                  productType
                }
            }
        }`

    return axios({
      url,
      method: 'post',
      data: {
        query: createMutation
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.createOffer)
        : console.error(res.data.errors)
    )
  },

  saveContactDetails: (
    uuid: String,
    contactDetails: ContactDetailInput,
    callback: (result: OfferInput) => void
  ) => {
    const saveContactDetailsMutation = `mutation{
          saveContactDetails(
            offerUuid: "${uuid}",
            contactDetails: {
              greeting: ${contactDetails.greeting}
              firstName: "${contactDetails.firstName}"
              lastName: "${contactDetails.lastName}"
              email: "${contactDetails.email}"
              phone: "${contactDetails.phone}"
            }
          ) {
            uuid
          }
        }`

    //console.log(saveContactDetailsMutation)
    return axios({
      url,
      method: 'post',
      data: {
        query: saveContactDetailsMutation
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.saveContactDetails)
        : console.error(res.data.errors)
    )
  },

  changeOfferFuneralType: (
    uuid: String,
    funeralType: FuneralType,
    callback: (result: OfferInput) => void
  ) => {
    const changeOfferFuneralTypeMutation = `mutation{
          changeOfferFuneralType(uuid: "${uuid}", funeralType: ${funeralType}) {
            uuid
          }
        }`

    return axios({
      url,
      method: 'post',
      data: {
        query: changeOfferFuneralTypeMutation
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.changeOfferFuneralType)
        : console.error(res.data.errors)
    )
  },

  applyPackage: (
    offerUuid: String,
    packageId: String,
    callback: (result: OfferInput) => void
  ) => {
    const applyPackageMutation = `mutation{
          applyPackage(offerUuid: "${offerUuid}", packageId: "${packageId}") {
            name
          }
        }`

    return axios({
      url,
      method: 'post',
      data: {
        query: applyPackageMutation
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.applyPackage)
        : console.error(res.data.errors)
    )
  },

  sendCardToPrint: (
    uuid: String,
    cardUuid: String,
    callback: (result: Product) => void
  ) => {

    const sendCardToPrint = `mutation{
      sendPrintConfirmation(offerUuid: "${uuid}", cardUuid: "${cardUuid}"){
        uuid
        status
        quantity
        productType
        prices{
          description
          currency
          amount
        }
        total{
          amount
        }
        photos
        orderPriority
        name
        longDescription
        externalUrl
      }
    }`

    return axios({
      url,
      method: 'post',
      data: {
        query: sendCardToPrint
      }
    }).then((res) =>{
      if(res.data.data !== null){
        callback(res.data.data.sendPrintConfirmation)
      }else{
        console.error(res.data.errors)
      }}
    )
  },

  changeCardObituaryDesign: (
    uuid: String,
    cardUuid: String,
    callback: (result: OfferInput) => void
  ) => {
    const changeCardDesign = `mutation{
      createCardDesign(offerUuid: "${uuid}", cardUuid: "${cardUuid}")
    }`

    return axios({
      url,
      method: 'post',
      data: {
        query: changeCardDesign
      }
    }).then((res) =>{
      if(res.data.data !== null){
        callback(res.data.data.createCardDesign)
      }else{
        console.error(res.data.errors)
      }}
    )
  },

  findAvailableGraveyards: (
    funeralType: FuneralType,
    centerLat: string,
    centerLong: string,
    callback: (result: Graveyard[]) => void
  ) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                    findGraveyards(funeralType: ${funeralType}, latitude: "${centerLat}", longitude: "${centerLong}"){
                    name,
                    latitude,
                    longitude,
                    uuid,
                    region,
                    country,
                    address,
                    zipCode
                  }
                }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.findGraveyards)
        : console.error(res.data.errors)
    )
  },

  findProductsByType: (
    productType: ProductType,
    funeralType: FuneralType,
    offerUuid: string,
    callback: (result: Product[]) => void
  ) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                    findProducts(productType: ${productType}, funeralType: ${funeralType}, offerUuid: "${offerUuid}") {
                      uuid
                      name
                      orderPriority
                      shortDescription
                      longDescription
                      prices {
                        description
                        currency
                        amount
                      }
                      photos
                      quantity
                      productType
                      availableFor
                      category
                    }
                  }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.findProducts)
        : console.error(res.data.errors)
    )
  },

  findProducts: (
    productType: ProductType,
    offerUuid: string,
    callback: (result: Product[]) => void
  ) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                    findProductByType(productType: ${productType}, offerUuid: "${offerUuid}") {
                      uuid
                      name
                      orderPriority
                      shortDescription
                      longDescription
                      prices {
                        description
                        currency
                        amount
                      }
                      photos
                      quantity
                      productType
                      availableFor
                      category
                    }
                  }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.findProductByType)
        : console.error(res.data.errors)
    )
  },

  findProductsByTypes: (
    productTypes: ProductType[],
    funeralType: FuneralType,
    offerUuid: string,
    callback: (result: Product[]) => void
  ) => {
    let queries = ''
    productTypes.forEach((type) => {
      queries += `${type}: findProducts(productType:${type},
        funeralType: ${funeralType}, offerUuid: "${offerUuid}"){
                uuid
                name
                orderPriority
                shortDescription
                longDescription
                prices {
                  description
                  currency
                  amount
                }
                photos
                quantity
                productType
                availableFor
                category
              }`
    })
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                    ${queries}
                  }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data)
        : console.error(res.data.errors)
    )
  },

  findFuneralHomeInfo: (uuid: string, callback: (result: any) => void) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                  fetchFuneralHomeInfo(offerUuid: "${uuid}") {
                    name
                    description
                    image
                    address {
                      street
                      number
                      zipCode
                      city
                    }
                    contactDetails {
                      firstName
                      lastName
                      email
                      phone
                      website
                    }
                  }
                }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data)
        : console.error(res.data.errors)
    )
  },

  findInterests: (uuid: string, callback: (result: any) => void) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                  findInterests(offerUuid: "${uuid}") {
                    uuid
                    name
                    shortDescription
                    longDescription
                    photos
                  }
                }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data)
        : console.error(res.data.errors)
    )
  },

  findPackages: (uuid: string, callback: (result: any) => void) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                  findPackages(uuid: "${uuid}") {
                    id                    
                    name
                    description
                    image
                  }
                }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data)
        : console.error(res.data.errors)
    )
  },

  sendContactForm: (
    contacTFormParams: ContactFormParams,
    callback: (result: any) => void
  ) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `
                mutation {
                    sendContactForm(params: {
                      name: "${contacTFormParams.name}"
                      email: "${contacTFormParams.email}"
                      subject: "${contacTFormParams.subject}"
                      message: "${contacTFormParams.message}"
                      phone: "${contacTFormParams.phone}"
                    }){
                      sent
                      message
                    }
                  }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data.sendContactForm)
        : console.error(res.data.errors)
    )
  },

  sendProvisionSignUpForm: (email) =>
    axios({
      url,
      method: 'post',
      data: {
        query: `
                mutation {
                  sendProvisionSignUpForm(email:"${email}"){
                    sent                    
                  }
                }`
      }
    }),

  sendBenuBoxSignUpForm: (email) =>
    axios({
      url,
      method: 'post',
      data: {
        query: `
                mutation {
                  sendBenuBoxSignUpForm(email:"${email}"){
                    sent                    
                  }
                }`
      }
    }),

  sendCallbackRequest: ({
    name,
    lastName,
    email,
    phone,
    callBackTime,
    contactReason
  }) =>
    axios({
      url,
      method: 'post',
      data: {
        query: `
                mutation {
                  sendCallbackRequest(params: {
                    name:"${name}"
                    lastName: "${lastName}"
                    email:"${email}"
                    phone: "${phone}"
                    contactReason: "${contactReason}"
                    callBackTime: "${callBackTime}"
                  }){
                    sent
                    message
                  }
                }`
      }
    }),

  confirmOffer: (uuid, callback) =>
    axios({
      url,
      method: 'post',
      data: {
        query: `
        mutation {
          confirmOffer(offerUuid: "${uuid}") {
            uuid
          }
        }`
      }
    }).then((res) => {
      if (res.data.data !== null) {
        callback(res)
      } else {
        console.error(res.data.errors)
        callback(false)
      }
    }),

  sendSharingEmail: (uuid, email, callback) =>
    axios({
      url,
      method: 'post',
      data: {
        query: `
    mutation {
      sendOfferSharingEmail(offerUuid: "${uuid}", emailList: "${email}")
    }`
      }
    }).then((res) => {
      if (
        res.data.data !== null &&
        res.data.data.sendOfferSharingEmail.indexOf('SUCCESS') >= 0
      ) {
        callback(res)
      } else {
        console.error(res.data.errors)
        callback(false)
      }
    }),

  sendMandate: (uuid, mandateForm: MandateForm, callback) =>
    axios({
      url,
      method: 'post',
      data: {
        query: `
        mutation {
          submitMandate(mandate: {
              offerUuid: "${uuid || ''}"
              greeting: ${mandateForm.customer_greeting}
              firstName: "${mandateForm.customer_firstname}"
              lastName: "${mandateForm.customer_lastname}"
              birthDate: "${mandateForm.customer_dateOfBirth}"
              address: "${mandateForm.customer_address}"
              relationship: "${mandateForm.customer_relation}"
              base64Signature: "${mandateForm.signature}"
              deceasedGreeting: ${mandateForm.deceased_greeting}
              deceasedFirstName: "${mandateForm.deceased_firstname}"
              deceasedLastName: "${mandateForm.deceased_lastname}"
              deceasedBirthDate: "${mandateForm.deceased_dateOfBirth}"
              deceasedObitDate: "${mandateForm.deceased_dateOfDeath}"
              deceasedReligionType: ${mandateForm.deceased_religionType}
              deceasedBirthPlace: "${mandateForm.deceased_placeOfBirth}"
              deceasedAddress: "${mandateForm.deceased_lastAddress}"
              deceasedFamilyStatus: ${mandateForm.deceased_familiyStatus}
              location: "${mandateForm.place}"
              clever: ${mandateForm.clever}
          })
        }`
      }
    }).then((res) => {
      if (res.data.data !== null) {
        callback(res)
      } else {
        console.error(res.data.errors)
        callback(null)
      }
    }),

  scheduleAppointment: (uuid, date, time, type) =>
    axios({
      url,
      method: 'post',
      data: {
        query: `
        mutation {
          scheduleAppointment(appointment: {
            offerUuid: "${uuid}"
            date: "${date}"
            type: ${type}
            time: ${time}
          })
        }
        `
      }
    }),

  searchTrack: (term: string, callback: (result: any) => void) => {
    return axios({
      url,
      method: 'post',
      data: {
        query: `{
                  trackSearch(term: "${term}") {
                    artist
                    name
                  }
                }`
      }
    }).then((res) =>
      res.data.data !== null
        ? callback(res.data.data)
        : console.error(res.data.errors)
    )
  }
}

export default backend



// WEBPACK FOOTER //
// ./src/api/index.ts